import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { buildApiUrl } from "../../../config"; // Assuming this is already configured
import ReturnModal from "./ReturnModal";

import OrderItemsTable from "./OrderItemsTable";
import { ColorRing, InfinitySpin } from "react-loader-spinner";
import convertToCr from "../../ui/convertToCr";
const OrderStatusTable = () => {
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [returnData, setReturnData] = useState({
    warehouseName: "",
    items: [{ defectiveQuantities: "", okayQuantities: "" }],
  });
  const userRole = localStorage.getItem("role");
  const orderDeliveredStatusEnum = [
    "Pending",
    "Dispatched",
    "Partial Dispatched",
    "Fully Delivered",
    "Return",
  ];

  const toggleRowExpansion = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  useEffect(() => {
    fetchOrderStatuses();
  }, []);

  const fetchOrderStatuses = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        buildApiUrl("/api/orderItem/order-status/details"),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort order statuses by createdAt (assuming createdAt is a valid date)
        const sortedData = data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setOrderStatuses(sortedData);
      } else {
        setError("Invalid response format. Expected an array.");
      }
    } catch (error) {
      setError("An error occurred while fetching order statuses.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter order statuses based on multiple fields
  const filteredOrderStatuses = orderStatuses.filter(
    (order) =>
      order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrderStatuses.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredOrderStatuses.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleReturnStatus = async (order) => {
    setSelectedOrder(order);
    setLoading(true);

    const token = localStorage.getItem("token");
    try {
      // Fetch the order details from the API
      const response = await fetch(
        buildApiUrl(`/api/orderItem/orderforstatus/${order.orderId}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data?.order?.items) {
        // Set the return data with the items from the fetched order details
        setReturnData({
          warehouseName: data.order.warehouseName,
          items: data.order.items.map((item) => ({
            categoryName: item.categoryName,
            itemCode: item.itemCode,
            itemName: item.itemName,
            itemqty: item.qty,
            defectiveQuantities: item.defectiveQuantities || "",
            okayQuantities: item.okayQuantities || "",
          })),
        });
      } else {
        toast.error("Failed to load order details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching order details.");
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };

  const updateOrderDeliveryStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem("token");
    const data = {
      orderDeliveredStatus: newStatus,
    };

    try {
      setLoading(true);
      const response = await fetch(
        buildApiUrl(`/api/orderItem/${orderId}/delivery-status`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (response.ok) {
        // Update the local state after successful status update
        setOrderStatuses((prevOrders) =>
          prevOrders.map((order) =>
            order.orderId === orderId
              ? { ...order, orderDeliveredStatus: newStatus }
              : order
          )
        );
        toast.success(`Order status updated to ${newStatus}`);
      } else {
        toast.error("Failed to update order status. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the status.");
    } finally {
      setLoading(false);
    }
  };

  const handleReturnSubmit = async () => {
    if (
      !returnData.items ||
      returnData.items.length === 0 ||
      !returnData.warehouseName
    ) {
      toast.error("Please provide return details and warehouse name.");
      return;
    }

    const updatedItems = returnData.items.map((item) => ({
      ...item,
      defectiveQuantities: Number(item.defectiveQuantities),
      okayQuantities: Number(item.okayQuantities),
    }));

    const token = localStorage.getItem("token");
    const data = {
      orderDeliveredStatus: "Return",
      warehouseName: returnData.warehouseName, // Include warehouseName in the request
      items: updatedItems,
    };

    try {
      setLoading(true);
      const response = await fetch(
        buildApiUrl(`/api/orderItem/${selectedOrder.orderId}/delivery-status`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (response.ok) {
        setOrderStatuses((prevOrders) =>
          prevOrders.map((order) =>
            order.orderId === selectedOrder.orderId
              ? { ...order, orderDeliveredStatus: "Return" }
              : order
          )
        );
        toast.success("Order marked for return successfully!");
        setShowModal(false);
        setReturnData({
          items: [{ defectiveQuantities: "", okayQuantities: "" }],
        }); // Reset state after submission
      } else {
        toast.error("Failed to process return. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred while processing the return.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setReturnData({ items: [{ defectiveQuantities: "", okayQuantities: "" }] }); // Reset data when modal is closed
  };

  // Pass the callback to the child component
  const handleStatusChange = (orderId, newStatus) => {
    fetchOrderStatuses();
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg">
      <div className="">
        {/* Search Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search Orders..."
            className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>

        {/* Loading/Error State */}
        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div
            className="overflow-x-auto max-w-[100vw] h-[40rem] rounded-lg"
            style={{ scrollbarWidth: "thin" }}
          >
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white">
                <tr>
                  <th className="p-2 text-left font-medium tracking-wide">#</th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Created At
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Order ID
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Client Name
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Total Amount
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Order Status
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Created By
                  </th>
                  <th className="p-2 text-left font-medium tracking-wide">
                    Assigned To
                  </th>
                  <th className="p-2 text-center font-medium tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.map((order, i) => (
                  <React.Fragment key={order.orderId}>
                    <tr
                      className={`hover:bg-gray-100 border border-b divide-x divide-gray-200 text-gray-700 border-collapse border-gray-200 transition-colors duration-200 cursor-pointer ${
                        expandedOrderId === order.orderId ? "bg-gray-100" : ""
                      }`}
                      onClick={() => toggleRowExpansion(order.orderId)} // Toggle on click
                    >
                      <td className="px-2 py-0.5">{i + 1}</td>
                      <td className="px-2 py-0.5">
                        {new Date(order.createdAt)
                          .toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true, // 12-hour format
                            timeZone: "Asia/Kolkata", // Indian Standard Time
                          })
                          .replace(",", "")}
                      </td>

                      <td className="px-2 py-0.5 text-blue-600 uppercase font-medium">
                        {order.orderId.slice(-5)}
                      </td>
                      <td className="px-2 py-0.5">{order.clientName}</td>
                      <td className="px-2 py-0.5 text-red-700  font-semibold ">
                        ₹{convertToCr(order.totalProductAmount)}
                      </td>
                      <td className="px-2 py-0.5">
                        <span
                          className={`px-2 py-0.5 rounded-md ${
                            order.orderStatus === "Accept"
                              ? "bg-green-100 text-green-700"
                              : order.orderStatus === "Reject"
                              ? "bg-red-100 text-red-600"
                              : "bg-orange-100 text-orange-600"
                          }`}
                        >
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="px-2 py-0.5">{order.createdBy}</td>
                      <td className="px-2 py-0.5">{order.assignedTo}</td>
                      <td className="px-2 space-x-1 text-center">
                        {/* Expand Details Link */}
                        {/* <Link
                            to={`/${userRole === "admin" ? 'admin' : 'sales-executive'}/order/${order.orderId}`}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            Expand Details
                          </Link> */}
                        {/* Delivery Status Dropdown */}
                        <select
                          value={order.orderDeliveredStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus === "Return") {
                              handleReturnStatus(order); // Open return modal if selected
                            } else {
                              updateOrderDeliveryStatus(
                                order.orderId,
                                newStatus
                              );
                            }
                          }}
                          className={`px-2 py-0.5 border border-gray-300 rounded-md ${
                            order.orderDeliveredStatus === "Delivered"
                              ? "bg-green-100 text-green-700"
                              : order.orderDeliveredStatus === "Dispatched"
                              ? "bg-blue-100 text-blue-700"
                              : order.orderDeliveredStatus ===
                                "Partial Dispatched"
                              ? "bg-blue-100 text-blue-700"
                              : order.orderDeliveredStatus === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : order.orderDeliveredStatus === "Cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                          } ${
                            order.orderStatus === "Pending" ||
                            order.orderStatus === "Rejected"
                              ? "bg-red-200 text-red-700 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            order.orderStatus === "Pending" ||
                            order.orderStatus === "Rejected"
                          } // Disable if Pending or Rejected
                        >
                          {orderDeliveredStatusEnum.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* Expanded Row - Show Order Items Table if the row is expanded */}
                    {expandedOrderId === order.orderId && (
                      <tr>
                        <td colSpan="9">
                          <OrderItemsTable
                            orderId={order.orderId}
                            onStatusChange={handleStatusChange}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-4 flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-500">
              Showing {indexOfFirstItem + 1} to{" "}
              {Math.min(indexOfLastItem, orderStatuses.length)} of{" "}
              {orderStatuses.length} orders
            </span>
          </div>

          <div className="flex space-x-2">
            {/* Previous Page Button */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg disabled:bg-gray-400"
            >
              Prev
            </button>
            {/* Next Page Button */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 text-gray-600 rounded-lg disabled:bg-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Modal for Return */}
      {showModal && (
        <ReturnModal
          returnData={returnData}
          setReturnData={setReturnData}
          handleModalClose={handleModalClose}
          handleReturnSubmit={handleReturnSubmit}
        />
      )}

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
};

export default OrderStatusTable;
